import fetchJson from '@atlassian/jira-fetch/src/utils/as-json.tsx';
import { fireErrorAnalytics } from '@jira/platform__error-reporting/src/fireErrorAnalytics.tsx';
import { safeStringify } from '@jira/platform__error-reporting/src/safeStringify.tsx';
import { getDocument } from '@jira/ssr__browser-globals/src/getDocument.tsx';
import { getWindow } from '@jira/ssr__browser-globals/src/getWindow.tsx';

type SpaWrmContext = {
	data: { [_: string]: unknown };
	errors: { [_: string]: unknown };
	resources: SpaWrmResource[];
};

type SpaWrmResource = {
	batchType: string;
	url: string;
	key: string;
	resourceType: string;
};

const SPA_WRM_CONTEXT_ENDPOINT = '/rest/frontend/1/spawrmcontext';

const fetchSpaWrmContext = async (): Promise<SpaWrmContext> => {
	return fetchJson<SpaWrmContext>(SPA_WRM_CONTEXT_ENDPOINT);
};

const createResourceElement = ({ url, resourceType, key, batchType }: SpaWrmResource) => {
	const document = getDocument();
	if (!document) {
		return undefined;
	}
	let element;
	if (resourceType === 'JAVASCRIPT') {
		// WRM's client context supplies required Jira resources, not an independently injected script.
		// eslint-disable-next-line jira/js/no-document-script
		element = document.createElement('script');
		element.setAttribute('crossorigin', '');
		element.setAttribute('src', url);
		element.setAttribute('data-client-bootstrapped', '');
		element.setAttribute('data-wrm-key', key);
		element.setAttribute('data-wrm-batch-type', batchType.toLowerCase());
	} else {
		element = document.createElement('link');
		element.setAttribute('crossorigin', '');
		element.setAttribute('href', url);
		element.setAttribute('rel', 'stylesheet');
		element.setAttribute('media', 'all');
		element.setAttribute('data-client-bootstrapped', '');
		element.setAttribute('data-wrm-key', key);
		element.setAttribute('data-wrm-batch-type', batchType.toLowerCase());
	}
	return element;
};

let hasWrmInitialised: Promise<void> | undefined;

const runBootstrap = () => {
	if (!getDocument() || !getWindow()) {
		return Promise.resolve();
	}
	const spaWrmContext = fetchSpaWrmContext();

	return new Promise<void>((resolve, reject) => {
		spaWrmContext
			.then(({ data, errors, resources }) => {
				// oxlint-disable-next-line no-multi-assign
				const browserWindow = getWindow();
				if (!browserWindow) {
					resolve();
					return;
				}
				const WRM = browserWindow.WRM || {};
				browserWindow.WRM = WRM;
				WRM._unparsedData = data;
				WRM._unparsedErrors = errors;
				if (WRM._dataArrived) WRM._dataArrived();

				resources
					.map(createResourceElement)
					.forEach((element) => element && getDocument()?.head.appendChild(element));

				resolve();
			})
			.catch((error) => {
				fireErrorAnalytics({
					error: new Error(safeStringify(error)),
					meta: {
						id: 'bootstrap-wrm-client',
						packageName: 'common-bridge',
						teamName: 'jfp-magma',
					},
				});
				reject(new Error('Failed to fetch the spa wrm context'));
			});
	});
};

const bootstrapWrmClient = (): Promise<void> => {
	if (__SERVER__) {
		return Promise.resolve();
	}
	if (hasWrmInitialised) {
		return hasWrmInitialised;
	}

	// todo maybe a more robust check to validate whether WRM initialisation already underway?
	if (getWindow()?.WRM !== undefined) {
		hasWrmInitialised = Promise.resolve();
	} else {
		hasWrmInitialised = runBootstrap();
	}
	return hasWrmInitialised;
};

export { bootstrapWrmClient };
